import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  DIRECT_BUY_ACCENT,
  DIRECT_BUY_BG,
  EDITABLE_BORDER,
  EMPHASIS_BG,
  IDEAL_BG,
  PROJECTION_COLOR,
  PROJECTION_LABEL,
  PROJECTION_ZERO_COLOR,
  PROJECTION_ZERO_LABEL,
  ProjectionOutcome,
  STATUS_COLOR,
  STATUS_LABEL,
  TONE_STYLE,
} from "./columns";
import { EnrichedRow } from "./derive";

type WIPStatus = EnrichedRow["status"];

/** Qué significa cada color de fila (columna «Estado»), en el orden en que avanza una línea. */
const STATUS_MEANING: [WIPStatus, string][] = [
  ["SIN_FECHAS", "el objetivo no tiene fecha de inicio o de fin."],
  ["PENDIENTE_INICIO", "todavía no ha llegado la fecha de inicio."],
  ["INICIO_PENDIENTE", "ya debería haber empezado, pero aún no hay volumen."],
  [
    "ATRASADO",
    "el volumen obtenido hasta ayer va más de un 10 % por debajo del esperado hasta ayer (Pacing % < 90).",
  ],
  [
    "EN_RITMO",
    "el volumen obtenido hasta ayer está dentro de ±10 % del esperado hasta ayer.",
  ],
  [
    "ADELANTADO",
    "el volumen obtenido hasta ayer va más de un 10 % por encima del esperado hasta ayer (Pacing % > 110).",
  ],
  ["FINALIZADA", "ya ha pasado la fecha de fin."],
];

/** Columna «Proyección»: el volumen al cierre si cada día que queda entrega
 *  lo mismo que ayer, frente al previsto. */
const PROJECTION_MEANING: [ProjectionOutcome, string, string][] = [
  [
    "CORRECTA",
    "→ +4%",
    "a este ritmo acabaría dentro de ±10 % del volumen previsto.",
  ],
  [
    "SE_PASA",
    "↗ +18%",
    "acabaría más de un 10 % por encima del previsto: se puede frenar.",
  ],
  [
    "NO_LLEGA",
    "↘ −25%",
    "acabaría más de un 10 % por debajo del previsto: hay que acelerar.",
  ],
];

const Swatch: React.FC<{ sx: object; children?: React.ReactNode }> = ({
  sx,
  children,
}) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      width: 44,
      height: 20,
      borderRadius: 0.5,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 600,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Item: React.FC<{
  swatch: React.ReactNode;
  children: React.ReactNode;
}> = ({ swatch, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    {swatch}
    <Typography variant="caption" sx={{ lineHeight: 1.45 }}>
      {children}
    </Typography>
  </Box>
);

const ButtonSwatch: React.FC<{ color: string; icon: React.ReactNode }> = ({
  color,
  icon,
}) => (
  <Swatch
    sx={{
      width: 64,
      backgroundColor: color,
      color: "#fff",
      gap: 0.25,
      "& svg": { fontSize: 13 },
    }}
  >
    {icon}
    Aceptar
  </Swatch>
);

/** Qué significa cada color y marca de la tabla. */
const WipLegend: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const tone = TONE_STYLE[mode];

  const heading = (text: string) => (
    <Typography
      variant="caption"
      sx={{
        gridColumn: "1 / -1",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: theme.palette.grey[600],
        mt: 0.5,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        py: 2,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Leyenda
      </Typography>
      <Typography
        variant="caption"
        component="p"
        sx={{ color: theme.palette.grey[600], mb: 1.5 }}
      >
        Solo aparecen briefings vigentes: la última versión de cada uno y sin
        los archivados.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          columnGap: 4,
          rowGap: 1.25,
        }}
      >
        {heading("Margen ideal")}
        <Item
          swatch={
            <Swatch
              sx={{
                backgroundColor: IDEAL_BG[mode],
                outline: `1px dashed ${EDITABLE_BORDER[mode]}`,
                outlineOffset: "-1px",
                color: EDITABLE_BORDER[mode],
              }}
            >
              <EditIcon sx={{ fontSize: 12 }} />
            </Swatch>
          }
        >
          <strong>Margen % (ideal)</strong>: se calcula solo con el coste real
          hasta ayer: 1 − Inv. Plat. Nec. (Total) / Inversión Cliente. Clic para
          editarlo; mientras escribes, Inv. Plat. Nec. (Total) enseña la
          inversión que dejaría ese margen.
        </Item>
        <Item
          swatch={
            <Swatch
              sx={{ backgroundColor: tone.bad.bg, color: tone.bad.color }}
            >
              −5%
            </Swatch>
          }
        >
          <strong>Margen ideal en rojo</strong>: negativo, la plataforma
          necesita más de lo que paga el cliente. No se puede aceptar tal cual:
          edítalo a un margen entre 0 y 100.
        </Item>
        <Item
          swatch={
            <ButtonSwatch
              color={theme.palette.primary.main}
              icon={<CloudUploadIcon />}
            />
          }
        >
          <strong>Aceptar (azul)</strong>: guarda el margen ideal desde mañana
          (desde hoy si es el último día) hasta el fin de la campaña y envía la
          inversión al presupuesto de la IO en DV360. Si la IO sube y tiene line
          items activos con presupuesto fijo, se les sube también (en proporción
          a su presupuesto, editable antes de confirmar). Si DV360 rechaza algo,
          no se guarda el margen.
        </Item>
        <Item
          swatch={
            <ButtonSwatch
              color={theme.palette.warning.main}
              icon={<CheckIcon />}
            />
          }
        >
          <strong>Aceptar (naranja)</strong>: guarda el margen ideal, pero la
          inversión hay que cambiarla a mano en la plataforma. Pasa en Nat Ads y
          el resto de plataformas que no son DV360, en las líneas de DV360 a
          nivel line item y cuando varias líneas comparten la misma IO.
        </Item>
        <Item
          swatch={
            <ButtonSwatch
              color={theme.palette.grey[400]}
              icon={<CheckIcon />}
            />
          }
        >
          <strong>Aceptar (gris)</strong>: no se puede aceptar tal cual. El
          margen está fuera de 0–100 o dejaría la inversión por debajo de lo ya
          gastado. El motivo sale al pasar el ratón.
        </Item>
        <Item
          swatch={
            <Swatch sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
              <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
            </Swatch>
          }
        >
          <strong>Line items</strong>: en las líneas de DV360 a nivel IO, la
          flecha despliega los line items con vuelo en las fechas de la línea
          (los de otros vuelos de la IO, con un botón) y su gasto, volumen y
          presupuesto en DV360. Si alguno tiene presupuesto fijo, subir solo la
          IO no basta: al aceptar se propone subirle también el suyo.
        </Item>
        <Item
          swatch={
            <Swatch sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
              —
            </Swatch>
          }
        >
          <strong>Sin botón ni margen ideal</strong>: todavía no hay coste real,
          la campaña ya ha terminado, la línea no está en producción o tu rol no
          puede cambiar márgenes.
        </Item>

        {heading("Color de fila (estado de la línea)")}
        {STATUS_MEANING.map(([status, meaning]) => (
          <Item
            key={status}
            swatch={
              <Swatch
                sx={{
                  backgroundColor: alpha(STATUS_COLOR[status], 0.16),
                  borderLeft: `3px solid ${STATUS_COLOR[status]}`,
                  borderRadius: 0,
                }}
              />
            }
          >
            <strong>{STATUS_LABEL[status]}</strong>: {meaning}
          </Item>
        ))}

        {heading("Proyección (volumen al cierre)")}
        {PROJECTION_MEANING.map(([outcome, example, meaning]) => (
          <Item
            key={outcome}
            swatch={
              <Swatch
                sx={{
                  width: 52,
                  backgroundColor: PROJECTION_COLOR[outcome],
                  color: "#fff",
                }}
              >
                {example}
              </Swatch>
            }
          >
            <strong>{PROJECTION_LABEL[outcome]}</strong>: {meaning}
          </Item>
        ))}
        <Item
          swatch={
            <Swatch
              sx={{
                width: 52,
                backgroundColor: PROJECTION_ZERO_COLOR,
                color: "#fff",
              }}
            >
              0
            </Swatch>
          }
        >
          <strong>{PROJECTION_ZERO_LABEL}</strong>: ayer no llegó nada, así que
          no se proyecta. La línea está parada o aún no se ha sincronizado.
        </Item>

        {heading("Compras directas")}
        <Item
          swatch={
            <Swatch
              sx={{
                backgroundColor: DIRECT_BUY_BG[mode],
                borderLeft: `3px solid ${DIRECT_BUY_ACCENT}`,
                borderRadius: 0,
              }}
            />
          }
        >
          <strong>Compra directa</strong>: línea activa sin conexión a ninguna
          plataforma. Sus datos los da el partner y se rellenan a mano; el color
          sustituye al del estado.
        </Item>
        <Item
          swatch={
            <Swatch
              sx={{
                backgroundColor: DIRECT_BUY_BG[mode],
                outline: `1px dashed ${EDITABLE_BORDER[mode]}`,
                outlineOffset: "-1px",
                color: EDITABLE_BORDER[mode],
              }}
            >
              <EditIcon sx={{ fontSize: 12 }} />
            </Swatch>
          }
        >
          <strong>Dato editable</strong>: impresiones, clics, visualizaciones,
          conversiones y coste de plataforma reales, como total acumulado hasta
          ayer. El coste es neto: el report le aplica el margen del brief. Vacío
          borra el dato. El resto de columnas se recalcula solo.
        </Item>

        {heading("Resto de marcas")}
        <Item swatch={<Swatch sx={{ backgroundColor: EMPHASIS_BG[mode] }} />}>
          <strong>Columna clave</strong> del seguimiento: estado, variación de
          volumen y pacing de inversión.
        </Item>
        <Item
          swatch={
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Swatch
                sx={{ backgroundColor: tone.good.bg, color: tone.good.color }}
              >
                OK
              </Swatch>
              <Swatch
                sx={{ backgroundColor: tone.warn.bg, color: tone.warn.color }}
              >
                !
              </Swatch>
              <Swatch
                sx={{ backgroundColor: tone.bad.bg, color: tone.bad.color }}
              >
                ✕
              </Swatch>
            </Box>
          }
        >
          <strong>Semáforo</strong>: verde en objetivo, ámbar a vigilar, rojo
          fuera. Pacing: ±10 % / ±25 % del 100 %. Volumen obtenido: ≥90 % / ≥70
          %. Variaciones: verde si suma, rojo si resta.
        </Item>
        <Item
          swatch={
            <Swatch sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
              —
            </Swatch>
          }
        >
          <strong>Sin dato</strong>: la línea aún no lo tiene (p. ej. sin coste
          real todavía).
        </Item>
      </Box>
    </Box>
  );
};

export default WipLegend;
